import React, { useEffect } from "react";

import {
  Button,
  CheckForm,
  Group,
  Page,
  Select,
  Typography,
} from "@samskip/frontend-components";
import { navigate } from "gatsby";
import { useTranslation } from "react-i18next";

import {
  BookingFilterByCustomers,
  BookingTemplateConstants,
  NavigationConstants,
} from "@assets/constants";
import { useAppDispatch, useAppSelector } from "@assets/hooks";
import { SelectOption } from "@assets/types";
import {
  BookingTemplateListFilter,
  BookingTemplatesTable,
} from "@components/index";
import UserService from "@services/UserService";
import {
  filterBookingTemplateSortBy,
  getTemplates,
  bookingTemplateFiltersState as bookingTemplateFiltersState,
  bookingTemplatesState as bookingTemplatesState,
  filterBookingTemplateCustomer,
  filterBookingTemplatesReset,
} from "@state/slices/bookingTemplateSlice";
import { userProfileState } from "@state/slices/userProfileSlice";

const BookingTemplatesList = () => {
  const { t } = useTranslation();
  const {
    consigneeCode,
    shipperCode,
    searchString,
    shippingTypes,
    sortBy,
    page,
    pageSize,
    templateCustomer,
  } = useAppSelector(bookingTemplateFiltersState);
  const { totalCount } = useAppSelector(bookingTemplatesState);
  const dispatch = useAppDispatch();
  const isEmployee = UserService.isUserEmployee();
  const isAgent = UserService.isUserAgent();
  const isAdmin = UserService.isUserAdmin();

  const handleOptionChange = (optionId: BookingFilterByCustomers) => {
    dispatch(filterBookingTemplateCustomer(optionId));
    dispatch(filterBookingTemplatesReset());
  };

  const handleSortByChange = (selectedValue: SelectOption<string>) => {
    dispatch(filterBookingTemplateSortBy(selectedValue.value));
  };

  const { userProfile } = useAppSelector(userProfileState);

  useEffect(() => {
    dispatch(getTemplates());
  }, [
    consigneeCode,
    shipperCode,
    searchString,
    shippingTypes,
    sortBy,
    page,
    pageSize,
    templateCustomer,
    userProfile?.SelectedCompany.PartnerCode,
  ]);

  return (
    <Page
      actions={
        <Button
          dataTestId="create-template-button"
          onClick={() =>
            navigate(
              `${NavigationConstants.NAVIGATION_LINKS.bookingTemplates}/create`
            )
          }
          variant="secondary"
        >
          {t("LABEL_CREATE_TEMPLATE")}
        </Button>
      }
      size="medium"
      subtitle={
        isEmployee || isAgent || isAdmin ? (
          <Group>
            <CheckForm
              checked={
                templateCustomer === BookingFilterByCustomers.CHOSEN_CUSTOMER
              }
              id="chosenCustomer"
              label={t("LABEL_CHOSEN_CUSTOMER")}
              name="bookingTemplates"
              onChange={() =>
                handleOptionChange(BookingFilterByCustomers.CHOSEN_CUSTOMER)
              }
              type="radio"
            />
            <CheckForm
              checked={
                templateCustomer === BookingFilterByCustomers.ALL_CUSTOMERS
              }
              id="allCustomers"
              label={t("LABEL_ALL_CUSTOMERS")}
              name="bookingTemplates"
              onChange={() =>
                handleOptionChange(BookingFilterByCustomers.ALL_CUSTOMERS)
              }
              type="radio"
            />
          </Group>
        ) : null
      }
      title={t("LABEL_LIST_BOOKING_TEMPLATE")}
    >
      <div className="flex flex-col gap-8">
        <BookingTemplateListFilter />
        <div className="flex items-baseline justify-between">
          {totalCount > 0 && (
            <Typography variant="label">
              {t("LABEL_N_M_OF_TOTAL_TEMPLATES", {
                n: (page - 1) * pageSize + 1,
                m: page * pageSize < totalCount ? page * pageSize : totalCount,
                total: totalCount,
              })}
            </Typography>
          )}
          {totalCount === 0 && (
            <p className="text-neutral-800">{t("LABEL_NO_TEMPLATES")}</p>
          )}
          <div className="flex items-baseline">
            <Typography className="pr-2" variant="label">
              {`${t("LABEL_SORT_BY")}:`}
            </Typography>
            <Select
              className="w-60"
              defaultValue={BookingTemplateConstants.SORT_BY_VALUES[0]}
              getOptionLabel={(sortByOption) => t(sortByOption.label)}
              id="sort-by"
              isSearchable={false}
              onChange={handleSortByChange}
              options={BookingTemplateConstants.SORT_BY_VALUES}
            />
          </div>
        </div>
        <BookingTemplatesTable />
      </div>
    </Page>
  );
};

export default BookingTemplatesList;
